import type { Edge, Node } from "reactflow";

export type DisconnectedNode = { id: string; type: string; label?: string };

/**
 * Find nodes that are NOT connected to any trigger node.
 *
 * Reachability is computed UNDIRECTED (an edge connects its two endpoints
 * regardless of handle direction) so we only flag genuinely orphaned nodes —
 * e.g. an Agent node dragged onto the canvas but never wired to the Trigger —
 * rather than nagging about edge-direction subtleties.
 *
 * Rules:
 *  - Trigger nodes are the roots and are never reported.
 *  - If there are no trigger nodes, returns [] (the separate NO_TRIGGER
 *    validation covers that case; we don't want to double-report).
 *
 * See FE #185 — the builder gave no warning when a node was disconnected from
 * the Trigger, letting users deploy a non-functioning bot.
 */
export function findDisconnectedNodes(
  nodes: Node[] | undefined,
  edges: Edge[] | undefined,
  triggerType = "trigger"
): DisconnectedNode[] {
  const nodeList = nodes ?? [];
  const edgeList = edges ?? [];

  const triggerIds = nodeList.filter((n) => n.type === triggerType).map((n) => n.id);
  if (triggerIds.length === 0) return [];

  // Undirected adjacency.
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    adjacency.get(a)!.add(b);
  };
  for (const e of edgeList) {
    if (!e?.source || !e?.target) continue;
    link(e.source, e.target);
    link(e.target, e.source);
  }

  // BFS from every trigger node.
  const reachable = new Set<string>(triggerIds);
  const queue = [...triggerIds];
  while (queue.length) {
    const current = queue.shift()!;
    for (const neighbour of adjacency.get(current) ?? []) {
      if (!reachable.has(neighbour)) {
        reachable.add(neighbour);
        queue.push(neighbour);
      }
    }
  }

  return nodeList
    .filter((n) => n.type !== triggerType && !reachable.has(n.id))
    .map((n) => ({
      id: n.id,
      type: n.type ?? "",
      label: (n.data?.label as string | undefined) || n.id,
    }));
}
